import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { logout } from "../../services/auth";
import WebSocketStatus from "../WebSocketStatus/WebSocketStatus";
import {
  fetchFunds,
  formatFundsData,
  formatCurrency,
  fetchChargesSummary,
  refreshOrders,
} from "../../services/funds";
import { isAuthenticated } from "../../services/auth";
import { fetchDummyPositions } from "../../services/optionChain";
import { useToast } from "../Toast/ToastContext";
import eventBus, { EVENTS } from "../../services/eventBus";

import "./TopBar.css";

const TopBar = ({
  onPanelChange,
  activePanel,
  maxLotsData,
  onRefreshMaxLots,
  selectedIndex,
  selectedExpiry,
}) => {
  const [fundsData, setFundsData] = useState(null);
  const [fundsLoading, setFundsLoading] = useState(false);
  const [chargesData, setChargesData] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [refreshingOrders, setRefreshingOrders] = useState(false);
  const [refreshingFunds, setRefreshingFunds] = useState(false);

  // Settings dropdown state
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [generatingDummyPositions, setGeneratingDummyPositions] = useState(false);
  const settingsDropdownRef = useRef(null);
  const { showSuccess, showError } = useToast();

  const handleClick = useCallback(
    (panel) => {
      onPanelChange(panel);
    },
    [onPanelChange],
  );

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch funds data
  const loadFunds = useCallback(async () => {
    if (!isAuthenticated()) return;

    setFundsLoading(true);
    try {
      const data = await fetchFunds();
      const formattedData = formatFundsData(data);
      setFundsData(formattedData);
    } catch (error) {
      console.error("Error loading funds:", error);
      // Silently fail - don't show error in top bar
    } finally {
      setFundsLoading(false);
    }
  }, []);

  // Fetch charges summary
  const loadCharges = useCallback(async () => {
    if (!isAuthenticated()) return;

    setChargesLoading(true);
    try {
      const data = await fetchChargesSummary();
      setChargesData(data);
    } catch (error) {
      console.error("Error loading charges:", error);
    } finally {
      setChargesLoading(false);
    }
  }, []);

  // Refresh orders from Zerodha
  const handleRefreshOrders = useCallback(async () => {
    if (!isAuthenticated() || refreshingOrders) return;

    setRefreshingOrders(true);
    try {
      const data = await refreshOrders();
      if (data.summary) {
        setChargesData(data.summary);
      }
    } catch (error) {
      console.error("Error refreshing orders:", error);
    } finally {
      setRefreshingOrders(false);
    }
  }, [refreshingOrders]);

  // Refresh funds from Zerodha
  const handleRefreshFunds = useCallback(async () => {
    if (!isAuthenticated() || refreshingFunds) return;

    setRefreshingFunds(true);
    try {
      await loadFunds();
    } catch (error) {
      console.error("Error refreshing funds:", error);
    } finally {
      setRefreshingFunds(false);
    }
  }, [refreshingFunds, loadFunds]);

  // Load funds on component mount and set up auto-refresh
  useEffect(() => {
    if (isAuthenticated()) {
      loadFunds();
      loadCharges();

      // Auto-refresh every 60 seconds
      const interval = setInterval(() => {
        loadFunds();
        loadCharges();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [loadFunds, loadCharges]);

  // Listen for order placement events to refresh charges (local DB only, no Zerodha API call)
  useEffect(() => {
    const unsubscribe = eventBus.on(EVENTS.ORDER_PLACED_SUCCESS, () => {
      // Small delay to allow backend to process the order
      setTimeout(() => {
        loadCharges();
      }, 500);
    });

    return () => unsubscribe();
  }, [loadCharges]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, []);

  // Handler for generating dummy positions
  const handleGenerateDummyPositions = useCallback(async () => {
    if (!selectedIndex || !selectedExpiry) {
      showError("Please select both index and expiry first");
      return;
    }

    if (!isAuthenticated()) {
      showError("Authentication required");
      return;
    }

    setGeneratingDummyPositions(true);
    try {
      const result = await fetchDummyPositions(selectedIndex, selectedExpiry);
      if (result.success) {
        showSuccess(`Dummy positions created (${result.data.positions_created})`);
        setShowSettingsDropdown(false);
      } else {
        showError(`Failed: ${result.message}`);
      }
    } catch (error) {
      showError(`Error: ${error.message}`);
    } finally {
      setGeneratingDummyPositions(false);
    }
  }, [selectedIndex, selectedExpiry, showSuccess, showError]);

  return (
    <div className="top-bar">
      <div className="nav-links">
        <Link
          to="/home"
          className={`nav-link ${activePanel === "home" ? "active" : ""}`}
          onClick={() => handleClick("home")}
        >
          Home
        </Link>
        <Link
          to="/orders"
          className={`nav-link ${activePanel === "orders" ? "active" : ""}`}
          onClick={() => handleClick("orders")}
        >
          Orders
        </Link>
        <Link
          to="/positions"
          className={`nav-link ${activePanel === "positions" ? "active" : ""}`}
          onClick={() => handleClick("positions")}
        >
          Positions
        </Link>
        <Link
          to="/portfolio"
          className={`nav-link ${activePanel === "portfolio" ? "active" : ""}`}
          onClick={() => handleClick("portfolio")}
        >
          Portfolio
        </Link>
        <Link
          to="/strategies"
          className={`nav-link ${activePanel === "strategies" ? "active" : ""}`}
          onClick={() => handleClick("strategies")}
        >
          Strategies
        </Link>
      </div>

      <div className="user-info">
        {/* Charges Display */}
        {chargesData && chargesData.net_charges !== undefined && (
          <div className="charges-display">
            <div className="charges-row">
              <span className="charges-label">Day Charges:</span>
              <span className="charges-total">
                {formatCurrency(chargesData.net_charges)}
              </span>
            </div>
            <div className="charges-row charges-breakdown">
              <span className="charges-breakdown-label">Orders:</span>
              <span className="charges-breakdown-value">
                <span className="orders-success">
                  {chargesData.successful_orders || 0}
                </span>
                {" / "}
                <span className="orders-failed">
                  {chargesData.failed_orders || 0}
                </span>
              </span>
            </div>
            <button
              className={`refresh-button ${refreshingOrders ? "refreshing" : ""}`}
              onClick={handleRefreshOrders}
              disabled={refreshingOrders}
              title="Refresh orders from Zerodha"
            >
              <i
                className={`fas fa-sync-alt ${refreshingOrders ? "fa-spin" : ""}`}
              ></i>
            </button>
          </div>
        )}
        {chargesLoading && (
          <div className="charges-loading">
            <span>Loading charges...</span>
          </div>
        )}

        {/* Funds Display */}
        {fundsData && fundsData.equity.enabled && (
          <div className="funds-display">
            <div className="funds-row">
              <span className="funds-label">Total:</span>
              <span className="funds-collateral">
                {formatCurrency(fundsData.equity.collateral)}
              </span>
            </div>
            <div className="funds-row">
              <span className="funds-label">Cash:</span>
              <span className="funds-amount">
                {formatCurrency(fundsData.equity.cash)}
              </span>
            </div>
            <div className="funds-row">
              <span className="funds-label">Used:</span>
              <span className="funds-used">
                {formatCurrency(fundsData.equity.used_debits)}
                {fundsData.equity.collateral > 0 && (
                  <span className="funds-percent">
                    ({((fundsData.equity.used_debits / fundsData.equity.collateral) * 100).toFixed(1)}%)
                  </span>
                )}
              </span>
            </div>
            <div className="funds-row">
              <span className="funds-label">Remaining:</span>
              <span className="funds-remaining">
                {formatCurrency(fundsData.equity.remaining)}
                {fundsData.equity.collateral > 0 && (
                  <span className="funds-percent">
                    ({((fundsData.equity.remaining / fundsData.equity.collateral) * 100).toFixed(1)}%)
                  </span>
                )}
              </span>
            </div>
            <button
              className={`refresh-button ${refreshingFunds ? "refreshing" : ""}`}
              onClick={handleRefreshFunds}
              disabled={refreshingFunds}
              title="Refresh funds from Zerodha"
            >
              <i
                className={`fas fa-sync-alt ${refreshingFunds ? "fa-spin" : ""}`}
              ></i>
            </button>
          </div>
        )}
        {fundsData && fundsData.equity.enabled && (
          <div className="max-lots-display">
            <div className="max-lots-row">
              <span className="max-lots-label">Max Lots:</span>
              <span className="max-lots-value">
                {maxLotsData?.loading ? "..." : maxLotsData?.max_lots || 0}
              </span>
            </div>
            <button
              className={`refresh-button ${maxLotsData?.loading ? "refreshing" : ""}`}
              onClick={onRefreshMaxLots}
              disabled={
                maxLotsData?.loading ||
                !selectedIndex ||
                !selectedExpiry ||
                !isAuthenticated()
              }
              title={`Refresh max lots for ${selectedIndex || "selected index"}`}
            >
              <i
                className={`fas fa-sync-alt ${maxLotsData?.loading ? "fa-spin" : ""}`}
              ></i>
            </button>
          </div>
        )}
        {fundsLoading && (
          <div className="funds-loading">
            <span>Loading...</span>
          </div>
        )}
        <WebSocketStatus />

        {/* Settings Dropdown */}
        <div className="settings-dropdown-container" ref={settingsDropdownRef}>
          <button
            className="settings-button"
            onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
            title="Settings"
          >
            <i className="fas fa-cog"></i>
          </button>

          {showSettingsDropdown && (
            <div className="settings-dropdown-menu">
              <button
                className="settings-menu-item"
                onClick={handleGenerateDummyPositions}
                disabled={generatingDummyPositions || !selectedIndex || !selectedExpiry}
              >
                <i className="fas fa-flask"></i>
                {generatingDummyPositions ? "Generating..." : "Generate Dummy Positions"}
              </button>
            </div>
          )}
        </div>

        <button className="logout-button" onClick={handleLogout} title="Logout">
          <i className="fas fa-sign-out-alt"></i>
        </button>
      </div>
    </div>
  );
};

export default React.memo(TopBar);
